/**
 * WEB-16 срез 2: показ цитаты и панель «отвечаю на …».
 *
 * Кусок ОБЩИЙ для всех лент — владельца (`msgChat`), участника (`memberChat`)
 * и комнаты (`roomChat`, срез 3) — намеренно, по тому же доводу, что у
 * `attachmentsUi`: у цитаты нет «стороны», и две копии рендера разошлись бы
 * первой же правкой, а вместе с ними разошлось бы и правило, ЧЕЙ текст
 * показывать — снимок отправителя или локальный оригинал.
 *
 * 🔴 Правило показа (дизайн §1.2). Снимок в сообщении — это СЛОВА
 * ОТПРАВИТЕЛЯ: расшифровка подтверждает, кто прислал само сообщение, и молчит
 * о цитате внутри. Поэтому:
 *
 * - оригинал нашёлся в СВОЕЙ переписке — показываем ЕГО текст и его автора
 *   (подделка не проходит у всех, у кого переписка есть, — это почти все);
 * - не нашёлся — показываем снимок (вошёл позже, другое устройство, истёк
 *   срок хранения). Отсутствие оригинала НЕ повод отвергнуть цитату: доказать,
 *   что id выдуман, мы не можем.
 *
 * Текст выводится обычными узлами React: ни HTML, ни разметки — это чужая
 * строка, а управляющие символы с неё сняты ещё контрактом (`replyTo.js`).
 */
import React, { useEffect, useRef, useState } from 'react';

import { ATTACHMENT_EXCERPT } from './replyTo';

/** Имя, которым подписывается цитата, когда своего оригинала нет и снимок
 *  пришёл без имени. В 1:1 это честно: автор оригинала — визави. */
const UNKNOWN_AUTHOR = 'Собеседник';

/** Текст сообщения для цитаты: у сообщения-файла тело пустое. */
function textOf(message) {
  if (message?.text?.trim()) return message.text;
  return message?.attachments?.length ? ATTACHMENT_EXCERPT : '';
}

/**
 * Что показать в цитате: локальный оригинал, если он есть, иначе снимок.
 *
 * @param quote — `replyTo` сообщения (уже прошедший контракт).
 * @param original — найденное локально сообщение или null/undefined.
 * @param selfName — как подписывать СВОИ сообщения («Вы»): подпись
 *        относительная и живёт только здесь, в отправку она не уезжает.
 * @param peerName — имя визави для входящих.
 */
export function quoteView(quote, original, {
  selfName = 'Вы', peerName = '', authorOf = null,
} = {}) {
  if (!quote) return null;
  if (original) {
    // В комнате авторов много, и имя берётся из РОСТЕРА по самому
    // сообщению (`authorOf`), а не из снимка отправителя: только так
    // подпись под цитатой нельзя подменить.
    const fromRoster = authorOf ? authorOf(original) : null;
    return {
      author: original.direction === 'out'
        ? selfName
        : (fromRoster || peerName || UNKNOWN_AUTHOR),
      text: textOf(original),
      fromSnapshot: false,
    };
  }
  return {
    author: quote.author || peerName || UNKNOWN_AUTHOR,
    text: quote.excerpt,
    fromSnapshot: true,
  };
}

/**
 * Блок цитаты НАД текстом сообщения в ленте.
 *
 * `original` ищет страница — по своей же ленте и только в ТЕКУЩЕЙ переписке
 * (глобальный поиск по id показал бы текст из чужого чата).
 */
export function ReplyQuote({ quote, original = null, names = {}, onJump = null }) {
  // Нажали на цитату, а оригинала у нас нет. Состояние местное: это не
  // ошибка переписки, а ответ на конкретное нажатие конкретного человека.
  const [missing, setMissing] = useState(false);
  const view = quoteView(quote, original, names);
  if (!view) return null;
  const click = () => {
    if (original) {
      setMissing(false);
      onJump?.(original.messageId);
      return;
    }
    // 🔴 Честная строка вместо тишины: у молчащего нажатия человек решит, что
    // сломано приложение, и будет жать ещё. Оригинала может не быть законно —
    // вошёл позже, другое устройство, истёк срок хранения.
    setMissing(true);
  };
  return (
    <span className="msgQuoteWrap">
      <button
        type="button"
        className={`msgQuote${view.fromSnapshot ? ' msgQuoteSnapshot' : ''}`}
        data-testid="msg-quote"
        // Кнопка, а не div с обработчиком: переход обязан работать с
        // клавиатуры и называться для чтения с экрана.
        aria-label={`Перейти к сообщению: ${view.author}, ${view.text || 'без текста'}`}
        onClick={click}
      >
        <span className="msgQuoteAuthor">{view.author}</span>
        <span className="msgQuoteText">
          {view.text || 'Сообщение без текста'}
        </span>
      </button>
      {missing && (
        <span className="msgQuoteMissing" role="status" data-testid="msg-quote-missing">
          Это сообщение недоступно — его нет на этом устройстве
        </span>
      )}
    </span>
  );
}

/**
 * Индекс ленты «id -> сообщение» для поиска оригинала цитаты.
 *
 * 🔴 Побеждает ПЕРВОЕ вхождение, и это не вкусовщина. `message_id` выбирает
 * клиент, и в комнате он может совпасть у чужого входящего и у моего
 * неотправленного (`roomController._loadHistory` описывает ровно этот случай).
 * Тогда в ленте ДВЕ строки с одним id: `new Map(...)` оставил бы последнюю, а
 * переход ищет по документу и находит первую — цитата показывала бы один
 * текст, а нажатие уводило к другому (находка ревью, Medium). Правило одно:
 * первая строка.
 */
export function indexMessages(messages) {
  const byId = new Map();
  for (const m of messages ?? []) {
    if (!byId.has(m.messageId)) byId.set(m.messageId, m);
  }
  return byId;
}

/** Сколько горит подсветка найденного оригинала, мс. */
export const JUMP_HIGHLIGHT_MS = 1600;

/**
 * Переход к оригиналу: прокрутка и короткая подсветка.
 *
 * 🔴 Узел ищется ПЕРЕБОРОМ по `data-message-id`, а не селектором из него.
 * `messageId` приходит из ЧУЖОГО шифротекста: подставленный в селектор, он
 * либо уронит запрос синтаксисом, либо выберет не тот узел. Перебор такой
 * возможности не оставляет вовсе (`CSS.escape` в старых Safari тоже нет).
 */
export function useQuoteJump(containerRef) {
  const [highlighted, setHighlighted] = useState(null);
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);
  const jumpTo = (messageId) => {
    const root = containerRef?.current;
    if (!root) return false;
    const node = [...root.querySelectorAll('[data-message-id]')]
      .find((el) => el.dataset.messageId === messageId);
    if (!node) return false;
    // jsdom не умеет scrollIntoView, да и старые браузеры не знают опций —
    // отсутствие прокрутки не должно ронять переход.
    node.scrollIntoView?.({ block: 'center' });
    // 🔴 Фокус переезжает ВМЕСТЕ с прокруткой (находка ревью, Medium): иначе
    // он остаётся на цитате за экраном — следующий Tab продолжает обход у
    // ответа, а чтение с экрана вообще не узнаёт, что мы куда-то перешли.
    // preventScroll: прокрутку уже сделали, второй рывок не нужен.
    node.focus?.({ preventScroll: true });
    setHighlighted(messageId);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setHighlighted(null), JUMP_HIGHLIGHT_MS);
    return true;
  };
  // 🔴 Снять подсветку снаружи нужно там, где ленты ДВЕ в одном экране
  // (участник): id могут совпасть, и метка осталась бы на чужом сообщении
  // после переключения канала (находка ревью, Low).
  const clearHighlight = () => {
    clearTimeout(timerRef.current);
    setHighlighted(null);
  };
  return { jumpTo, highlighted, clearHighlight };
}

/** Кнопка «ответить» у сообщения в ленте. */
export function ReplyButton({ onClick, disabled = false }) {
  return (
    <button
      type="button"
      className="msgReplyBtn"
      title="Ответить на это сообщение"
      aria-label="Ответить на это сообщение"
      data-testid="msg-reply-btn"
      disabled={disabled}
      onClick={onClick}
    >
      ↩
    </button>
  );
}

/**
 * Панель «отвечаю на …» над полем ввода, с крестиком.
 *
 * Показывается СНИМОК, который реально уедет: человек должен видеть то, что
 * увидит собеседник, а не свою локальную версию.
 */
export function ReplyDraftBar({ quote, onCancel, selfName = 'Вы' }) {
  if (!quote) return null;
  // 🔴 Отвечая СЕБЕ, человек обязан видеть «Вы», а не «Собеседник» (находка
  // ревью, Low). В снимок при этом уезжает ПУСТОЕ имя, и это не оплошность:
  // у получателя автором окажется его визави, а слово «Вы» значило бы у него
  // другого человека. Флаг `mine` — местный, до отправки он не доживает:
  // контракт (`normalizeReplyTo`) собирает новый объект из трёх ключей.
  const author = quote.mine ? selfName : (quote.author || UNKNOWN_AUTHOR);
  return (
    <div className="msgReplyDraft" data-testid="reply-draft">
      <span className="msgReplyDraftLabel">Ответ на</span>
      <span className="msgReplyDraftAuthor">{author}</span>
      <span className="msgReplyDraftText">{quote.excerpt || 'сообщение без текста'}</span>
      <button
        type="button"
        className="msgReplyDraftClear"
        title="Отменить ответ"
        aria-label="Отменить ответ"
        data-testid="reply-draft-clear"
        onClick={onCancel}
      >
        ✕
      </button>
    </div>
  );
}
